import { useCallback, useMemo, useState } from "react";
import axios from "axios";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import { ProgressCardProps } from "views/components/LabelPropagation/ProgressStep/ProgressCard";
import { ProgressCardsProps } from "views/components/LabelPropagation/ProgressStep";
import {
  LABEL_CLASS_PARAMETER,
  MANUAL_REVIEW_FLOW,
  REVIEW_PARAMETER,
  REVIEW_TAB_PARAMETER,
} from "../../helpers/constants/miscellaneous";
import { VLProgressBarProps } from "views/uikit/ProgressBars/VLProgressBar";
import { useSearchParams } from "react-router-dom";

export interface LabelStatsData extends StatData {
  category_display_name: string;
  label_category_id: string;
}

interface StatData {
  cycle_questions: number;
  cycle_questions_answered: number;
  goal: number;
  total_annotated: number;
  total_annotated_toward_goal: number;
  total_questions_asked: number;
}

export interface StatsData {
  stats: StatData;
  label_stats: LabelStatsData[];
  total_media_count: number;
  total_questions_to_answer: number;
}

export type StatsState = {
  mainProgress: VLProgressBarProps;
  labels: ProgressCardsProps;
  review: ProgressCardsProps;
};

// ---------- helpers ----------

const mapLabelItems = (data: LabelStatsData[]): ProgressCardProps[] =>
  data.map((stat) => ({
    label: stat.category_display_name,
    maxItems: stat.goal,
    itemLength: stat.total_annotated,
    id: stat.label_category_id,
  }));

const mapReviewItems = (
  data: LabelStatsData[],
  labelCategoryId: string | null,
  onClick: (id: string) => void,
): ProgressCardProps[] =>
  data.map((stat) => ({
    label: stat.category_display_name,
    maxItems: stat.cycle_questions,
    itemLength: stat.cycle_questions_answered,
    active: labelCategoryId === stat.label_category_id,
    onClick,
    id: stat.label_category_id,
  }));

const toProgressCardsBase = (
  maxItems: number,
  itemLength: number,
  items: ProgressCardProps[],
  totalCount: string,
): ProgressCardsProps => ({
  mainProgress: {
    maxItems,
    itemLength,
  },
  totalCount,
  items,
});

const toLabelProgressCards = (
  main: StatData,
  items: ProgressCardProps[],
): ProgressCardsProps => {
  const percent =
    main.goal > 0
      ? Math.floor((main.total_annotated_toward_goal / main.goal) * 100)
      : 0;

  return toProgressCardsBase(
    main.goal,
    main.total_annotated_toward_goal,
    items,
    `${percent}%`,
  );
};

const toReviewProgressCards = (
  main: StatData,
  items: ProgressCardProps[],
): ProgressCardsProps => {
  return toProgressCardsBase(
    main.cycle_questions,
    main.cycle_questions_answered,
    items,
    (main.cycle_questions - main.cycle_questions_answered).toString(),
  );
};

// ---------- hook ----------

export const useGetStats = () => {
  const [rawData, setRawData] = useState<StatsData | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState<boolean>();

  const [searchParams, setSearchParams] = useSearchParams();
  const labelId = searchParams.get(LABEL_CLASS_PARAMETER);

  const onReviewClicked = useCallback(
    (id: string) => {
      searchParams.set(LABEL_CLASS_PARAMETER, id);
      searchParams.set(REVIEW_PARAMETER, "true");
      searchParams.set(MANUAL_REVIEW_FLOW, "true");
      const labelStats = rawData?.label_stats.find(
        ({ label_category_id }) => label_category_id === id,
      );
      const isAllReviewedInLabel =
        labelStats &&
        labelStats.cycle_questions_answered === labelStats.cycle_questions;
      searchParams.set(
        REVIEW_TAB_PARAMETER,
        isAllReviewedInLabel ? "All" : "For Review",
      );
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams, rawData],
  );

  const fetchStats = useCallback(async (datasetId: string, flowId: string) => {
    try {
      setLoading(true);
      const statsUrl = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/flywheel/${datasetId}/stats/${flowId}`,
      );
      const { data } = await axios.get<StatsData>(statsUrl);
      setRawData(data);
      return data;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const stats: StatsState | undefined = useMemo(() => {
    if (!rawData) {
      return undefined;
    }

    const labels = toLabelProgressCards(
      rawData.stats,
      mapLabelItems(rawData.label_stats),
    );

    const review = toReviewProgressCards(
      rawData.stats,
      mapReviewItems(rawData.label_stats, labelId, onReviewClicked),
    );

    const mainProgressItemsLength =
      rawData.stats.total_questions_asked -
      rawData.stats.cycle_questions +
      rawData.stats.cycle_questions_answered;
    const mainProgressMaxItems = rawData.total_questions_to_answer;

    const mainProgress: VLProgressBarProps = {
      maxItems: mainProgressMaxItems,
      itemLength: mainProgressItemsLength,
    };

    return { labels, review, mainProgress };
  }, [rawData, labelId, onReviewClicked]);

  return {
    stats,
    fetchStats,
    error,
    loading,
  };
};
